<script setup lang="ts">
import { computed } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { GlobalSuggestionTypeSwitch } from '../globalSuggestionType';

interface ItineraryDateRangeProps {
  relativeDays: string;
  absoluteShortDays: string;
  isFirstSegment: boolean;
  secondSegmentIsNoTransport: boolean;
}

const props = defineProps<ItineraryDateRangeProps>();

const browserStore = useBrowserStore();

const shouldShowGlobalSuggestionSwitch = computed<boolean>(
  () => props.isFirstSegment && browserStore.isLargerThan599px
);
</script>

<template>
  <div class="itinerary-date-range-wrapper">
    <div
      class="itinerary-date-range"
      :class="[
        {
          before: !props.isFirstSegment,
          after:
            !props.isFirstSegment ||
            (props.isFirstSegment && !props.secondSegmentIsNoTransport),
        },
      ]"
    >
      <div class="relative-days-wrapper">
        <span class="day-label">{{ $t('planner.days') }}</span>
        <span class="relative-days-label">{{ props.relativeDays }}</span>
      </div>
      <div class="absolute-days-wrapper">{{ props.absoluteShortDays }}</div>
    </div>
    <GlobalSuggestionTypeSwitch
      v-if="shouldShowGlobalSuggestionSwitch"
      class="global-suggestion"
    />
  </div>
</template>

<style scoped lang="scss">
.itinerary-date-range-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .global-suggestion {
    margin-top: var(--spacing-8);
  }
}

.itinerary-date-range {
  --top-spacing: var(--spacing-24);
  --bottom-spacing: var(--spacing-16);

  position: relative;
  padding: var(--top-spacing) 0 var(--bottom-spacing) 0;
  width: fit-content;
  color: var(--color-white);
  display: flex;

  @include for-less-or-equal-than-599-px {
    --bottom-spacing: var(--spacing-32);
  }

  .relative-days-wrapper {
    background-color: var(--color-primary-50);
    padding: var(--spacing-4) var(--spacing-16) var(--spacing-4)
      var(--spacing-24);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-4);
    @include font-label-l-bold;

    @include for-less-or-equal-than-599-px {
      border-radius: 0;
      padding: var(--spacing-4) var(--spacing-12);
    }

    .relative-days-label {
      letter-spacing: var(--spacing-2);
    }
  }

  .absolute-days-wrapper {
    background-color: var(--color-primary-60);
    padding: var(--spacing-8) var(--spacing-16);
    border-radius: 0 var(--spacing-8) var(--spacing-8) 0;
    display: flex;
    align-items: center;
    @include font-label-l-bold;

    @include for-less-or-equal-than-599-px {
      padding: var(--spacing-8) var(--spacing-12);
    }
  }

  &.after::after {
    content: '';
    height: var(--bottom-spacing);
    width: 0px;
    border-right: 1px dashed var(--color-secondary-50);
    position: absolute;
    bottom: 0;
    left: 49px;

    @include for-less-or-equal-than-599-px {
      left: 37px;
    }
  }

  &.before::before {
    content: '';
    height: var(--top-spacing);
    width: 0px;
    border-right: 1px dashed var(--color-secondary-50);
    position: absolute;
    top: 0;
    left: 49px;

    @include for-less-or-equal-than-599-px {
      left: 37px;
      height: var(--top-spacing);
    }
  }

  @media print {
    .relative-days-wrapper,
    .absolute-days-wrapper {
      color: var(--color-primary-20);
    }
  }
}
</style>
